/**
 * STOKLAR — eski `stocks.php` karşılığı.
 *
 * Tek listede hem PARÇA (hammadde) hem MAMUL (bitmiş ürün) stoğu görünür.
 * Eski ekranda filtre, arama, sıralama ve sayfalama HİÇ YOKTU; tüm stok tek sayfaya basılıyordu.
 *
 * DÜZELTİLEN ESKİ HATALAR
 *  · `remove_stock.php` GET ile satırı komple SİLİYORDU (CSRF'e açık, miktar düşürmüyordu).
 *    Burada silme yoktur; miktar yalnızca `adjustStock` callable'ı ile ve GEREKÇE ile değişir,
 *    her hareket `inventoryMoves` defterine yazılır.
 *  · Negatif miktar girip stok düşürme hilesi yerine açık "Çıkış" yönü vardır.
 *  · Bileşen ALIŞ FİYATI yalnızca `costView` yetkisi olan kullanıcıya (boss + office) gösterilir
 *    ve ayrı `components/{id}/private/pricing` dokümanından okunur — üretim rolü göremez.
 *    Maliyet/kâr bu ekranda HİÇBİR ROLE gösterilmez.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doc, getDoc, limit as fsLimit, orderBy, type QueryConstraint } from "firebase/firestore";

import { DataTable } from "../components/DataTable";
import { Modal } from "../components/Modal";
import { Toast } from "../components/Toast";
import { Spinner } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import { useCollection } from "../hooks/useCollection";
import { useCallableAction } from "../hooks/useCallableAction";
import { useCan } from "../lib/auth";
import { db } from "../lib/firebase";
import { dt, n, tl } from "../lib/format";
import { errorText } from "../lib/errors";
import { PageHeader } from "../components";

interface InventoryRow {
  id: string;
  /** 'part' = Balder parçası, 'product'/'finishedGood' = mamul. */
  itemType?: string;
  refId?: string;
  name?: string;
  unit?: string;
  /** Göç betiği `volume`, fonksiyon prototipi `quantity` yazıyor — ikisi de okunur. */
  volume?: number;
  quantity?: number;
  minVolume?: number;
  isBelowMin?: boolean;
  updatedAt?: unknown;
  lastMovementAt?: unknown;
}

interface ComponentRow {
  id: string;
  name?: string;
  unit?: string;
  categoryId?: string;
  active?: boolean;
}

type TypeFilter = "all" | "part" | "product";

/**
 * EKSİK STOK KAYDI — `stockShortages/{productionId}`.
 *
 * ★ BU KAYIT AYLARDIR YAZILIYORDU AMA HİÇBİR EKRAN OKUMUYORDU.
 *
 * `stock/consume.ts`, üretim yetersiz stok yüzünden tamamlanamadığında buraya
 * kalıcı bir kayıt atıyor ve kendi yorumunda "Eksik stok GÖRÜNÜR olur" diyor;
 * CONTRACT.md §63 de bu koleksiyonu kullanıcıya gösterilen uyarı kayıtları
 * arasında sayıyor. Gerçekte görünen tek şey o ANDA çıkan bildirimdi: bildirim
 * okununca kaybolur, kalıcı kayıt ise Security Rules'ta hiç tanımlı olmadığı
 * için okunamazdı bile. Sonuç: "hangi üretim hangi malzeme yüzünden takıldı"
 * sorusunun yanıtı veritabanında duruyor, ekranda yoktu.
 *
 * ── NİÇİN SAYAÇ/ÖZET DEĞİL, DOĞRUDAN KAYIT ────────────────────────────────
 * Türetilmiş bir özet dokümanı (ör. `counters/shortages`) sapabilirdi — bu
 * projede tam olarak öyle oldu. Burada kaydın KENDİSİ okunur, açık/çözülmüş
 * ayrımı istemcide `resolved` alanından yapılır: sapacak bir kopya yoktur.
 */
interface ShortageLine {
  componentId?: string;
  name?: string;
  unit?: string;
  needed?: number;
  available?: number;
  missing?: number;
  /** false = stok kartı HİÇ açılmamış (stok bitmiş değil, veri girişi eksik). */
  hasInventoryRecord?: boolean;
}

interface ShortageRow {
  id: string;
  productionId?: string;
  sourceType?: string;
  sourceId?: string;
  sourceLabel?: string;
  shortages?: ShortageLine[];
  insufficientCount?: number;
  missingInventoryRecordCount?: number;
  resolved?: boolean;
  detectedAt?: unknown;
}

function volumeOf(r: InventoryRow): number {
  if (typeof r.volume === "number") return r.volume;
  if (typeof r.quantity === "number") return r.quantity;
  return 0;
}

function isPart(r: InventoryRow): boolean {
  return (r.itemType ?? (r.id.startsWith("part_") ? "part" : "product")) === "part";
}

export default function Stock() {
  const can = useCan();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [onlyProblem, setOnlyProblem] = useState(false);
  const [showPrices, setShowPrices] = useState(false);
  const [prices, setPrices] = useState<Record<string, number | null>>({});
  const [priceLoading, setPriceLoading] = useState(false);
  const [toast, setToast] = useState<{ kind: "success" | "error"; message: string } | null>(null);

  const [adjustRow, setAdjustRow] = useState<InventoryRow | null>(null);
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  // Kısıt yok: stok kalemi sayısı sınırlıdır ve filtreleme/sıralama istemcide yapılır.
  // (Varsayılan sıra doküman kimliğidir → part_* ve product_* birlikte gelir.)
  const invConstraints = useMemo<QueryConstraint[]>(() => [], []);
  const { data: inventory, loading, error } = useCollection<InventoryRow>("inventory", invConstraints);

  const compConstraints = useMemo<QueryConstraint[]>(() => [], []);
  const { data: components } = useCollection<ComponentRow>("components", compConstraints);

  /**
   * Eksik stok kayıtları. Sıralama alanı `detectedAt` CANLIDA her dokümanda
   * dolu (3/3 ölçüldü); eksik olsaydı o kayıt `orderBy`'dan sessizce düşerdi.
   * 100 kayıtla sınırlı: bu liste bir arşiv değil, "şu an ne takılı" tahtası.
   */
  const shortageConstraints = useMemo<QueryConstraint[]>(
    () => [orderBy("detectedAt", "desc"), fsLimit(100)],
    [],
  );
  const { data: shortageDocs, error: shortageError } = useCollection<ShortageRow>(
    "stockShortages",
    shortageConstraints,
  );
  /** Çözülmüş kayıt listeyi şişirmesin; `resolved` alanı yoksa AÇIK sayılır. */
  const openShortages = useMemo(
    () => shortageDocs.filter((s) => s.resolved !== true),
    [shortageDocs],
  );

  const componentById = useMemo(() => {
    const m = new Map<string, ComponentRow>();
    for (const c of components) m.set(c.id, c);
    return m;
  }, [components]);

  const adjustAction = useCallableAction<
    { inventoryId: string; delta: number; reason: string },
    { before: number; after: number }
  >("adjustStock");

  const rows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return inventory
      .map((r) => {
        const part = isPart(r);
        const comp = part && r.refId ? componentById.get(r.refId) : undefined;
        return {
          ...r,
          _isPart: part,
          _name: r.name ?? comp?.name ?? r.refId ?? r.id,
          _unit: r.unit ?? comp?.unit ?? "adet",
          _volume: volumeOf(r),
        };
      })
      .filter((r) => {
        if (typeFilter === "part" && !r._isPart) return false;
        if (typeFilter === "product" && r._isPart) return false;
        if (onlyProblem && !(r._volume < 0 || r.isBelowMin === true)) return false;
        if (q && !r._name.toLocaleLowerCase("tr").includes(q) && !r.id.includes(q)) return false;
        return true;
      })
      .sort((a, b) => a._name.localeCompare(b._name, "tr"));
  }, [inventory, componentById, search, typeFilter, onlyProblem]);

  const negativeCount = inventory.filter((r) => volumeOf(r) < 0).length;

  /** Alış fiyatları ayrı `private/pricing` dokümanlarındadır; yalnızca costView okuyabilir. */
  async function loadPrices() {
    if (!can.seeCost) return;
    setPriceLoading(true);
    try {
      const partIds = rows.filter((r) => r._isPart && r.refId).map((r) => r.refId!);
      const unique = [...new Set(partIds)].slice(0, 300);
      const entries = await Promise.all(
        unique.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, "components", id, "private", "pricing"));
            const v = snap.exists() ? snap.get("unitPrice") : null;
            return [id, typeof v === "number" ? v : null] as const;
          } catch {
            return [id, null] as const;
          }
        }),
      );
      setPrices(Object.fromEntries(entries));
      setShowPrices(true);
    } finally {
      setPriceLoading(false);
    }
  }

  async function submitAdjust() {
    if (!adjustRow) return;
    const raw = Number(amount.replace(",", "."));
    if (!Number.isFinite(raw) || raw <= 0) {
      setToast({ kind: "error", message: "Miktar sıfırdan büyük olmalıdır." });
      return;
    }
    if (reason.trim().length < 3) {
      setToast({ kind: "error", message: "Gerekçe zorunludur (en az 3 karakter)." });
      return;
    }
    const delta = Math.round(direction === "in" ? raw : -raw);
    try {
      const res = await adjustAction.run({
        inventoryId: adjustRow.id,
        delta,
        reason: reason.trim(),
      });
      // `run()` hata durumunda ASLA fırlatmaz, null döner (bkz. useCallableAction).
      // Null'ı sessizce geçmek "başarıyla güncellendi" yalanı olurdu.
      if (!res) return;
      setToast({
        kind: "success",
        message: `Stok güncellendi: ${n(res.before)} → ${n(res.after)}`,
      });
      setAdjustRow(null);
      setAmount("");
      setReason("");
    } catch (e) {
      setToast({ kind: "error", message: errorText(e, "Stok güncellenemedi.") });
    }
  }

  return (
    <PageHeader title="Stoklar">
      {toast ? <Toast kind={toast.kind} message={toast.message} onClose={() => setToast(null)} /> : null}

      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Parça veya ürün ara…"
          aria-label="Stok ara"
          className="min-h-[48px] w-full max-w-xs rounded-md border border-gray-300 px-4 text-base shadow-sm sm:w-auto"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          aria-label="Tür"
          className="min-h-[48px] rounded-md border-0 bg-white px-4 py-3 text-base font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300"
        >
          <option value="all">Tümü</option>
          <option value="part">Balder Parçası</option>
          <option value="product">Mamul Ürün</option>
        </select>
        <label className="inline-flex min-h-[48px] cursor-pointer items-center gap-2 rounded-md bg-white px-4 text-base font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300">
          <input
            type="checkbox"
            checked={onlyProblem}
            onChange={(e) => setOnlyProblem(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-blue-600"
          />
          Sorunlu stok
        </label>

        {can.seeCost ? (
          <button
            type="button"
            disabled={priceLoading}
            onClick={() => (showPrices ? setShowPrices(false) : void loadPrices())}
            className="inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            {priceLoading ? "Yükleniyor…" : showPrices ? "Alış fiyatlarını gizle" : "Alış fiyatlarını göster"}
          </button>
        ) : null}

        {/* Tüm hareket defteri — kaleme süzülmemiş hâli. Menüde karşılığı yok,
            giriş burasıdır. */}
        <Link
          to="/inventory/movements"
          className="ml-auto inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Stok Hareketleri
        </Link>

        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Yazdır
        </button>
      </div>

      {negativeCount > 0 ? (
        <div className="mb-4 rounded-lg bg-amber-50 p-4 text-base text-amber-900">
          {negativeCount} stok kalemi negatif. Negatif stok, üretimin yetersiz stokla bilinçli olarak
          tamamlandığını gösterir — sayım yapılıp düzeltilmelidir.
        </div>
      ) : null}

      {/*
        ★ EKSİK STOK PANOSU — sunucunun aylardır yazdığı, hiçbir ekranın
        okumadığı `stockShortages` kayıtları. Bildirim anlıktı; takılı kalan
        üretimin sebebi bildirim okununca kayboluyordu.

        "Stok kartı açılmamış" ile "stok yetersiz" AYRI sayılır: birincisi veri
        girişi işidir (bileşen tanımlı ama `inventory` kaydı hiç açılmamış),
        ikincisi satın alma işidir. Canlıda açık üç kaydın üçü de birinci
        türden — ofis olmayan bir sayımın peşine düşüyordu.
      */}
      {shortageError ? (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-base text-red-700">
          Eksik stok kayıtları okunamadı: {shortageError}
        </div>
      ) : null}

      {openShortages.length > 0 ? (
        <section
          aria-labelledby="eksik-stok-baslik"
          className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4"
        >
          <h2 id="eksik-stok-baslik" className="text-base font-semibold text-red-900">
            Stok yetersizliği yüzünden tamamlanamayan {openShortages.length} üretim
          </h2>
          <p className="mt-1 text-sm text-red-800">
            Bu kayıtlar üretim tamamlama denemesi sırasında sunucu tarafından açıldı ve üretim geri
            alınana kadar açık kalır. “Stok kartı yok” yazan satırda malzeme bitmiş değildir —
            o bileşenin <Link to="/inventory" className="underline">stok kaydı hiç açılmamıştır</Link>.
          </p>
          <ul className="mt-3 space-y-3">
            {openShortages.map((s) => {
              const lines = Array.isArray(s.shortages) ? s.shortages : [];
              const missingRecord = lines.filter((l) => l.hasInventoryRecord === false);
              const insufficient = lines.filter((l) => l.hasInventoryRecord !== false);
              return (
                <li key={s.id} className="rounded-md bg-white p-3 shadow-sm ring-1 ring-red-200">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold text-gray-900">
                      {s.sourceType === "order" && s.sourceId ? (
                        <Link to={`/orders/${s.sourceId}`} className="text-blue-700 underline">
                          {s.sourceLabel || `Sipariş ${s.sourceId}`}
                        </Link>
                      ) : s.productionId ? (
                        <Link to={`/production/${s.productionId}`} className="text-blue-700 underline">
                          {s.sourceLabel || s.productionId}
                        </Link>
                      ) : (
                        s.sourceLabel || s.id
                      )}
                    </span>
                    <span className="text-sm text-gray-500">{dt(s.detectedAt)}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-700">
                    {missingRecord.length > 0 ? `${missingRecord.length} bileşenin stok kartı yok` : null}
                    {missingRecord.length > 0 && insufficient.length > 0 ? " · " : null}
                    {insufficient.length > 0 ? `${insufficient.length} bileşende stok yetersiz` : null}
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
                    {lines.slice(0, 12).map((l, i) => (
                      <li key={`${s.id}-${l.componentId ?? i}`} className="tabular-nums">
                        <span className="font-medium">{l.name ?? l.componentId ?? "?"}</span>{" "}
                        {n(l.missing)} {l.unit ?? ""} eksik
                        {l.hasInventoryRecord === false ? (
                          <span className="ml-1 rounded bg-amber-100 px-1 text-xs font-semibold text-amber-900">
                            stok kartı yok
                          </span>
                        ) : null}
                      </li>
                    ))}
                    {lines.length > 12 ? (
                      <li className="text-gray-500">… ve {lines.length - 12} bileşen daha</li>
                    ) : null}
                  </ul>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-lg bg-red-50 p-4 text-base text-red-700">
          Stok listesi okunamadı: {error}
        </div>
      ) : null}

      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState title="Stok kaydı yok" description="Filtreleri temizleyip tekrar deneyin." />
      ) : (
        <DataTable
            rowKey={(r) => r.id}
            caption="Stok listesi"
          columns={[
            {
              key: "_name",
              header: "Ad",
              render: (r: (typeof rows)[number]) => (
                <span>
                  <span className="block text-base text-gray-900">{r._name}</span>
                  <span className="block text-xs text-gray-500">{r.id}</span>
                  {/* Telefonda gizlenen "Tür" sütunu burada görünür. */}
                  <span className="block text-xs text-gray-500 sm:hidden">
                    {r._isPart ? "Parça" : "Mamul"}
                  </span>
                </span>
              ),
            },
            {
              key: "_isPart",
              header: "Tür",
              // ★ TELEFON: 5 sütun 515px yer istiyordu (343px kap). İkincil
              //   sütunlar telefonda gizlenir, tablet ve masaüstü değişmez.
              hideOnMobile: true,
              render: (r: (typeof rows)[number]) => (r._isPart ? "Parça" : "Mamul"),
            },
            {
              key: "_volume",
              header: "Miktar",
              render: (r: (typeof rows)[number]) => (
                <span
                  className={
                    r._volume < 0
                      ? "text-base font-bold text-red-600"
                      : r.isBelowMin
                        ? "text-base font-semibold text-amber-600"
                        : "text-base text-gray-900"
                  }
                >
                  {n(r._volume, ` ${r._unit}`)}
                </span>
              ),
            },
            ...(can.seeCost && showPrices
              ? [
                  {
                    key: "_price",
                    header: "Alış Fiyatı",
                    render: (r: (typeof rows)[number]) => {
                      if (!r._isPart || !r.refId) return "—";
                      const p = prices[r.refId];
                      return p == null ? "—" : tl(p);
                    },
                  },
                ]
              : []),
            {
              key: "updatedAt",
              header: "Son hareket",
              hideOnMobile: true,
              render: (r: (typeof rows)[number]) => dt((r.lastMovementAt ?? r.updatedAt) as never),
            },
            {
              key: "actions",
              header: "",
              render: (r: (typeof rows)[number]) => (
                <span className="flex items-center justify-end gap-2 print:hidden">
                  {/*
                    ★ "Stok Hareketleri" ekranına GİRİŞ YOKTU.

                    `/inventory/movements` rotası App.tsx'te tanımlı ve ekran
                    çalışıyor, ama uygulamada oraya giden TEK BİR bağlantı bile
                    yoktu — yalnızca kendisinden çıkan "← Stoklar" bağlantısı
                    vardı. Üstelik ekran `?inventoryId=` parametresini okuyup
                    hareketleri tek kaleme süzüyor; bu parametreyi hiç kimse
                    göndermediği için kalem bazlı hareket dökümü hiç
                    kullanılmadı. Kalemin kimliği doğrudan `inventoryKey`dir
                    (`stock/consume.ts` `inventory` dokümanını bu anahtarla
                    açar), o yüzden satırın kimliği aynen geçirilir.
                  */}
                  <Link
                    to={`/inventory/movements?inventoryId=${encodeURIComponent(r.id)}`}
                    className="inline-flex min-h-[44px] items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Hareketler
                  </Link>
                  {can.isStaff ? (
                    <button
                      type="button"
                      onClick={() => {
                        setAdjustRow(r);
                        setDirection("in");
                        setAmount("");
                        setReason("");
                      }}
                      className="inline-flex min-h-[44px] items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                    >
                      Stok Hareketi
                    </button>
                  ) : null}
                </span>
              ),
            },
          ]}
          rows={rows}
        />
      )}

      <Modal
        open={adjustRow !== null}
        title={`Stok Hareketi — ${adjustRow?.name ?? adjustRow?.id ?? ""}`}
        onClose={() => setAdjustRow(null)}
      >
        <p className="mb-4 text-base text-gray-700">
          Her hareket kim/ne zaman/neden bilgisiyle hareket defterine yazılır. Satır silinmez.
        </p>

        <div className="mb-4 flex gap-2">
          <button
            type="button"
            onClick={() => setDirection("in")}
            className={`inline-flex min-h-[48px] flex-1 items-center justify-center rounded-md px-4 py-3 text-base font-semibold ${
              direction === "in"
                ? "bg-green-600 text-white"
                : "bg-white text-gray-700 ring-1 ring-inset ring-gray-300"
            }`}
          >
            Giriş (+)
          </button>
          <button
            type="button"
            onClick={() => setDirection("out")}
            className={`inline-flex min-h-[48px] flex-1 items-center justify-center rounded-md px-4 py-3 text-base font-semibold ${
              direction === "out"
                ? "bg-red-600 text-white"
                : "bg-white text-gray-700 ring-1 ring-inset ring-gray-300"
            }`}
          >
            Çıkış (−)
          </button>
        </div>

        <label htmlFor="stock-amount" className="block text-base font-semibold text-gray-800">
          Miktar
        </label>
        <input
          id="stock-amount"
          inputMode="numeric"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="mt-1 min-h-[48px] w-full rounded-md border border-gray-300 px-3 text-base"
        />

        <label htmlFor="stock-reason" className="mt-4 block text-base font-semibold text-gray-800">
          Gerekçe (zorunlu)
        </label>
        <textarea
          id="stock-reason"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Örn. Tedarikçi girişi, sayım farkı, fire."
          className="mt-1 w-full rounded-md border border-gray-300 p-3 text-base"
        />

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setAdjustRow(null)}
            className="inline-flex min-h-[48px] items-center rounded-md bg-white px-4 py-3 text-base font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
          >
            Vazgeç
          </button>
          <button
            type="button"
            disabled={adjustAction.loading}
            onClick={() => void submitAdjust()}
            className="inline-flex min-h-[48px] items-center rounded-lg bg-gray-900 px-5 py-3 text-base font-bold text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {adjustAction.loading ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </Modal>
    </PageHeader>
  );
}
